// Lifecycle reporting for the payment QR image (<QrCodeImage>).
//
// The QR is a network image proxied through the backend. When it never shows,
// the session log used to go silent between "Payment process started" and the
// guest quitting, so "QRIS doesn't appear" could not be told apart from "guest
// saw the QR and walked away". The reporter turns the image's lifecycle into a
// handful of discrete events (at most one per attempt, plus one slow-load
// warning and one exit line), and `describeQrLoadEvent` turns each into a
// single activity-log line with a stable message every payment surface shares.

/** Fires once per load cycle if the QR has neither loaded nor failed by then. */
export const QR_SLOW_LOAD_MS = 10_000

/** Host + path of the proxy URL, plus the upstream (Midtrans) host. Never the query. */
export interface QrTarget {
  qrHost: string
  qrPath: string
  upstreamHost?: string
}

/** What the browser tells us about the network when an attempt ends. */
export interface QrNetworkSnapshot {
  online?: boolean
  effectiveType?: string
}

interface QrEventBase {
  target: QrTarget
  /** 1-based <img> attempt within the current load cycle. */
  attempt: number
  /** ms since the cycle began (mount / new URL / manual reload). */
  ms: number
  /** How this cycle began. */
  trigger: 'mount' | 'manual'
}

export type QrLoadEvent =
  | (QrEventBase & { type: 'loaded' })
  | (QrEventBase & { type: 'attempt-failed'; network: QrNetworkSnapshot })
  | (QrEventBase & { type: 'gave-up'; network: QrNetworkSnapshot })
  | (QrEventBase & { type: 'slow'; network: QrNetworkSnapshot })
  | (QrEventBase & { type: 'manual-reload'; msSinceGaveUp: number })
  | (QrEventBase & {
      type: 'abandoned'
      reason: 'unmount' | 'url-change'
      state: 'loading' | 'gave-up'
    })

export type QrLoadEventHandler = (event: QrLoadEvent) => void

interface ReporterDeps {
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  network?: () => QrNetworkSnapshot
}

export interface QrLoadReporter {
  /** A new load cycle starts for `qrUrl` (first attempt in flight). */
  begin: (qrUrl: string, trigger?: 'mount' | 'manual') => void
  /** The <img> fired `load`. Only the first load of a cycle is reported. */
  loaded: () => void
  /** The <img> fired `error`; `willRetry` says whether an auto-retry follows. */
  failed: (willRetry: boolean) => void
  /** An auto-retry is about to swap in a fresh <img>. */
  retrying: () => void
  /** The guest/operator tapped the manual reload button. Starts a new cycle. */
  manualReload: () => void
  /** The component unmounts or the URL changes; reports if the QR never showed. */
  end: (reason: 'unmount' | 'url-change') => void
}

/** Strips the query: logs must never carry the full Midtrans URL. */
export function describeQrTarget(qrUrl: string): QrTarget {
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost/'
    const url = new URL(qrUrl, base)
    const target: QrTarget = { qrHost: url.host, qrPath: url.pathname }
    const upstream = url.searchParams.get('url')
    if (upstream) {
      try {
        target.upstreamHost = new URL(upstream).host
      } catch {
        // Not an absolute URL; leave the upstream host out rather than log it raw.
      }
    }
    return target
  } catch {
    return { qrHost: 'unparseable', qrPath: '' }
  }
}

function browserNetwork(): QrNetworkSnapshot {
  if (typeof navigator === 'undefined') return {}
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
    .connection
  return {
    online: navigator.onLine,
    ...(connection?.effectiveType ? { effectiveType: connection.effectiveType } : {})
  }
}

export function createQrLoadReporter(
  onEvent: QrLoadEventHandler,
  deps: ReporterDeps = {}
): QrLoadReporter {
  const now = deps.now ?? ((): number => Date.now())
  const setTimer = deps.setTimer ?? ((fn, ms): unknown => setTimeout(fn, ms))
  const clearTimer =
    deps.clearTimer ?? ((handle): void => clearTimeout(handle as ReturnType<typeof setTimeout>))
  const network = deps.network ?? browserNetwork

  let active = false
  let target: QrTarget = { qrHost: '', qrPath: '' }
  let trigger: 'mount' | 'manual' = 'mount'
  let startedAt = 0
  let attempt = 1
  let state: 'loading' | 'loaded' | 'gave-up' = 'loading'
  let gaveUpAt = 0
  let slowTimer: unknown = null

  const base = (): QrEventBase => ({ target, attempt, ms: now() - startedAt, trigger })

  const disarmSlow = (): void => {
    if (slowTimer !== null) {
      clearTimer(slowTimer)
      slowTimer = null
    }
  }

  const start = (nextTrigger: 'mount' | 'manual'): void => {
    disarmSlow()
    active = true
    trigger = nextTrigger
    startedAt = now()
    attempt = 1
    state = 'loading'
    slowTimer = setTimer(() => {
      slowTimer = null
      if (active && state === 'loading') onEvent({ ...base(), type: 'slow', network: network() })
    }, QR_SLOW_LOAD_MS)
  }

  return {
    begin: (qrUrl, nextTrigger = 'mount'): void => {
      target = describeQrTarget(qrUrl)
      start(nextTrigger)
    },
    loaded: (): void => {
      if (!active || state !== 'loading') return
      state = 'loaded'
      disarmSlow()
      onEvent({ ...base(), type: 'loaded' })
    },
    failed: (willRetry): void => {
      if (!active || state !== 'loading') return
      if (willRetry) {
        onEvent({ ...base(), type: 'attempt-failed', network: network() })
        return
      }
      state = 'gave-up'
      gaveUpAt = now()
      disarmSlow()
      onEvent({ ...base(), type: 'gave-up', network: network() })
    },
    retrying: (): void => {
      if (!active || state !== 'loading') return
      attempt += 1
    },
    manualReload: (): void => {
      if (!active) return
      onEvent({
        ...base(),
        type: 'manual-reload',
        msSinceGaveUp: state === 'gave-up' ? now() - gaveUpAt : 0
      })
      start('manual')
    },
    end: (reason): void => {
      if (!active) return
      disarmSlow()
      if (state !== 'loaded') {
        onEvent({ ...base(), type: 'abandoned', reason, state })
      }
      active = false
    }
  }
}

/** Every QR log line starts with this, so one prefix query finds them all. */
export const QR_LOG_PREFIX = 'Payment QR'

/**
 * One activity-log line per event. `surface` names the screen showing the QR
 * (e.g. 'bahela-payment', 'reprint') so a triage query can split by flow.
 */
export function describeQrLoadEvent(
  event: QrLoadEvent,
  surface: string
): { message: string; details: Record<string, unknown> } {
  const { target, ...rest } = event
  const details: Record<string, unknown> = { surface, ...target, ...rest }
  delete details.type
  if ('network' in event) {
    delete details.network
    Object.assign(details, event.network)
  }
  switch (event.type) {
    case 'loaded':
      return { message: `${QR_LOG_PREFIX} loaded`, details }
    case 'attempt-failed':
      return {
        message: `${QR_LOG_PREFIX} load failed, retrying`,
        details: { ...details, level: 'warn' }
      }
    case 'gave-up':
      return {
        message: `${QR_LOG_PREFIX} load gave up, reload button shown`,
        details: { ...details, level: 'error' }
      }
    case 'slow':
      return {
        message: `${QR_LOG_PREFIX} still not loaded after ${Math.round(QR_SLOW_LOAD_MS / 1000)}s`,
        details: { ...details, level: 'warn' }
      }
    case 'manual-reload':
      return { message: `${QR_LOG_PREFIX} manual reload clicked`, details }
    case 'abandoned':
      return {
        message: `${QR_LOG_PREFIX} never shown before leaving`,
        details: { ...details, level: 'warn' }
      }
  }
}

/** Adapter for the session store's `logActivity`, used by every payment surface. */
export function qrActivityLogger(
  logActivity: (message: string, details?: Record<string, unknown>) => void,
  surface: string
): QrLoadEventHandler {
  return (event): void => {
    const { message, details } = describeQrLoadEvent(event, surface)
    logActivity(message, details)
  }
}
